import { Box } from "../Box"
import { Button } from "../Button"
import { ButtonGroup } from "../ButtonGroup"
import { ConjoinedIcons } from "../ConjoinedIcons"
import { Icon, type IconName } from "../Icon"
import { Skeleton } from "../Skeleton"
import { Text } from "../Text"
import styles from "./LinkDescriptionCard.module.css"

export interface LinkDescriptionCardProps {
  url: URL | string
  title: string
  subTitle: string
  description?: string
  onEdit?: () => void
  onUnlink?: () => void
  largeIconName?: IconName
  loading?: boolean
  error?: boolean
}

export function LinkDescriptionCard(props: LinkDescriptionCardProps) {
  const {
    description,
    onEdit,
    largeIconName = "public",
    onUnlink,
    loading,
    error
  } = props

  return (
    <Box
      flexDirection="column"
      backgroundColor="grey1"
      borderRadius={6}
      boxShadow="3"
      border
      width={420}
      padding={16}
      gap={16}
    >
      <Box flexDirection="row" justifyContent="space-between">
        <Box gap={8} alignItems="center">
          {error ? (
            <LinkDescriptionErrorContent errorMessage="Failed to load linked document data" />
          ) : (
            <LinkDescriptionCardBody
              {...props}
              largeIconName={largeIconName}
              loading={loading}
            />
          )}
        </Box>

        <Box>
          <ButtonGroup size="medium" color="grey">
            {onEdit && <Button startIcon="edit" onClick={onEdit} />}
            {onUnlink && <Button startIcon="linkOff" onClick={onUnlink} />}
          </ButtonGroup>
        </Box>
      </Box>
      {!error && <Description description={description} loading={loading} />}
    </Box>
  )
}

interface TitleProps {
  url: URL | string
  title?: string
  loading?: boolean
}

function Title({ url, title, loading }: TitleProps) {
  if (loading) return <LinkDescriptionCardSkeleton height={16} width={80} />

  if (!title) return null

  return (
    <Text variant="smallBold" href={url.toString()}>
      {title}
    </Text>
  )
}

interface SubtitleProps {
  subTitle?: string
  loading?: boolean
}

function Subtitle({ subTitle, loading }: SubtitleProps) {
  if (loading)
    return (
      <Box padding={{ block: 2 }}>
        <LinkDescriptionCardSkeleton height={12} width={60} />
      </Box>
    )

  if (!subTitle) return null

  return (
    <Text variant="small" color="grey11">
      {subTitle}
    </Text>
  )
}

interface DescriptionProps {
  description?: string
  loading?: boolean
}

function Description({ description, loading }: DescriptionProps) {
  if (!description || loading) return null

  return (
    <Box>
      <Text variant="small" color="grey11">
        {description}
      </Text>
    </Box>
  )
}

interface LinkDescriptionCardBodyProps {
  largeIconName: IconName
  title: string
  subTitle: string
  url: string | URL
  loading?: boolean
}

function LinkDescriptionCardBody(props: LinkDescriptionCardBodyProps) {
  const { largeIconName, title, url, loading, subTitle } = props
  return (
    <>
      <ConjoinedIcons largeIconName={largeIconName} smallIconName="link" />
      <Box flexDirection="column">
        <Title title={title} url={url} loading={loading} />
        <Subtitle subTitle={subTitle} loading={loading} />
      </Box>
    </>
  )
}

function LinkDescriptionErrorContent({
  errorMessage
}: {
  errorMessage: string
}) {
  return (
    <Box alignItems="center" gap={8}>
      <div className={styles.circledIcon}>
        <Icon name="alert" color="tomato11" size="medium" />
      </div>
      <Text color="tomato11">{errorMessage}</Text>
    </Box>
  )
}

function LinkDescriptionCardSkeleton({
  width,
  height
}: {
  width: number
  height: number
}) {
  return (
    <Skeleton color="grey5" borderRadius={6} width={width} height={height} />
  )
}
